import { Box, Table, TableBody, TableContainer, TableRow, Typography } from "@mui/material";
import { styles } from "../stylesRightSidebar";
import { FC } from "react";
import { useNavigate } from "react-router-dom";
import { navigateWithParams } from "@/helpers/navigation";
import { TimeRangeValue } from "@/types/timeRanges";
import { normalizeSentiment } from "@/helpers/normalizeSentiment";
import BullSvg from "../../../../assets/svg/BullSvg";
import BearSvg from "../../../../assets/svg/BearSvg";
import { formatLikesCount } from "@/helpers/formatCount";

type Data = {
  name: string;
  symbol: string;
  posts: number;
  likes: number;
  sentiment?: number | null;
  industry?: string;
  sector?: string;
};

interface TrendingTableProps {
  premium: boolean;
  sortedData: Data[];
  sortOrder: "asc" | "desc";
  handleSort: () => void;
  activeSortTab: "post" | "likes" | "liked" | "sentiment";
  filterTime: TimeRangeValue;
  view: "all" | "follows";
  activeTab: "STOCK" | "SECTOR" | "INDUSTRY" | "ETF" | "INDEX" | "ALL";
  subtype: string[];
  subscriptionLoading: boolean;
}

const TrendingTable: FC<TrendingTableProps> = ({
  premium,
  sortOrder,
  sortedData,
  activeSortTab,
  filterTime,
  view,
  activeTab,
  subtype,
  subscriptionLoading,
}) => {
  const navigate = useNavigate();

  const handleSymbolClick = (row: Data) => {
    let whoParam = "";
    if (view.toLowerCase() === "follows") whoParam = "Following";
    else if (view.toLowerCase() === "all") whoParam = "All";
    else whoParam = view;

    const queryParams: Record<string, any> = {
      timeRange: filterTime,
      who: whoParam,
      orderType: "Top",
      subtype: subtype,
      sortBy: activeSortTab,
      order: sortOrder,
    };

    if (activeTab === "SECTOR" || activeTab === "INDUSTRY") {
      queryParams.type = activeTab.toLowerCase();
      queryParams.name = row.name;
      queryParams.orderType = "Top";
      if (activeTab === "INDUSTRY") {
        queryParams.industries = [row.name];
      }
      if (activeTab === "SECTOR") {
        queryParams.sectors = [row.name];
      }

      navigateWithParams(navigate, "trending", queryParams);
      return;
    }
    navigateWithParams(navigate, "stock", queryParams, row.symbol.toUpperCase());
  };

  return (
    <TableContainer sx={{ ...styles.tableContainer }}>
      <Table sx={{ width: "100%" }}>
        <TableBody sx={styles.tableBody}>
          {sortedData.map((row, index) => (
            <TableRow
              key={crypto.randomUUID()}
              sx={{
                filter: !premium && index >= 3 && !subscriptionLoading ? "blur(3px)" : "none",
                userSelect: premium ? "auto" : index >= 3 ? "none" : "auto",
                height: "73px",
                cursor: "pointer",
              }}
            >
              <Box
                onClick={() => (premium || index < 3 ? handleSymbolClick(row) : null)}
                sx={{
                  height: "73px",
                  width: "95%",
                  minWidth: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: "4px",
                  justifyContent: "center",
                  cursor: premium || index < 3 ? "pointer" : "default",
                }}
              >
                <Box sx={{ height: "16px", display: "flex", alignItems: "center", minWidth: 0 }}>
                  <Typography
                    sx={{
                      height: "16px",
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "#6E767D",
                      overflow: "hidden",
                      whiteSpace: "nowrap",
                      textOverflow: "ellipsis",
                      minWidth: 0,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {row.name}
                  </Typography>
                </Box>

                {row.symbol && (
                  <Box sx={{ height: "17px", display: "flex", alignItems: "center" }}>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: 600,
                        color: "#202327",
                        height: "17px",
                        display: "flex",
                        alignItems: "center",
                        width: "36px",
                      }}
                    >
                      {row.symbol}
                    </Typography>
                  </Box>
                )}

                <Box sx={{ height: "15px", display: "flex", alignItems: "center" }}>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#6E767D",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {row.posts} Posts
                  </Typography>
                  <Box sx={{ fontWeight: 500, color: "#6E767D", margin: "0 4px" }}>|</Box>
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      color: "#6E767D",
                      height: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {formatLikesCount(row.likes)} likes
                  </Typography>
                  {row.sentiment !== undefined && (
                    <>
                      <Box sx={{ fontWeight: 500, color: "#6E767D", margin: "0 4px" }}>|</Box>
                      <Typography
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          fontSize: "12px",
                          fontWeight: 500,
                          color:
                            normalizeSentiment(row.sentiment) === 0
                              ? "#6E767D"
                              : normalizeSentiment(row.sentiment) > 0
                                ? "#008001"
                                : "#CE212B",
                          gap: "4px",
                        }}
                      >
                        {normalizeSentiment(row.sentiment) > 0
                          ? `+${normalizeSentiment(row.sentiment)}`
                          : normalizeSentiment(row.sentiment)}

                        <Box sx={{ width: 20, height: 16 }}>
                          {normalizeSentiment(row.sentiment) === 0 ? (
                            <></>
                          ) : normalizeSentiment(row.sentiment) > 0 ? (
                            <BullSvg height={16} />
                          ) : (
                            <BearSvg width={20} height={10} />
                          )}
                        </Box>
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default TrendingTable;
